import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Pagination from '../../components/common/Pagination';
import toast from 'react-hot-toast';
import BillingSystemStatus from '../../components/billing/BillingSystemStatus';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface UsageDetails {
  metrics_events: number;
  log_events: number;
  trace_spans: number;
  storage_gb: number;
  avg_infra_hosts: number;
  avg_apm_hosts: number;
}

interface Invoice {
  id: string;
  tenant: {
    name: string;
    subdomain: string;
  };
  subscription: {
    plan: {
      name: string;
    };
  };
  invoice_number: string;
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE';
  subtotal: number;
  tax: number;
  total: number;
  due_date: string;
  paid_at: string | null;
  created_at: string;
  usage_details?: UsageDetails;
  line_items?: Array<{
    description: string;
    quantity: number;
    unit_price: number;
    total: number;
  }>;
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'paid' | 'void' | 'draft'>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/v1/billing/admin/invoices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Convert string values to numbers
        const invoicesWithNumbers = (data.data.invoices || []).map((inv: any) => ({
          ...inv,
          subtotal: Number(inv.subtotal || 0),
          tax: Number(inv.tax || 0),
          total: Number(inv.total || 0),
          usage_details: inv.usage_details ? {
            metrics_events: Number(inv.usage_details.metrics_events || 0),
            log_events: Number(inv.usage_details.log_events || 0),
            trace_spans: Number(inv.usage_details.trace_spans || 0),
            storage_gb: Number(inv.usage_details.storage_gb || 0),
            avg_infra_hosts: Number(inv.usage_details.avg_infra_hosts || 0),
            avg_apm_hosts: Number(inv.usage_details.avg_apm_hosts || 0),
          } : undefined,
        }));
        setInvoices(invoicesWithNumbers);
      }
    } catch (error) {
      console.error('Failed to load invoices', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    if (!confirm('Mark this invoice as paid? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/v1/billing/admin/invoices/${invoiceId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_method: 'BANK_TRANSFER',
          notes: 'Marked as paid by admin',
        }),
      });

      if (response.ok) {
        loadInvoices();
        setSelectedInvoice(null);
        toast.success('Invoice marked as paid successfully', {
          duration: 3000,
          icon: '✅',
        });
      } else {
        toast.error('Failed to mark invoice as paid', {
          duration: 5000,
          icon: '❌',
        });
      }
    } catch (error) {
      console.error('Failed to mark invoice as paid', error);
      toast.error('Error marking invoice as paid', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleVoid = async (invoiceId: string) => {
    if (!confirm('Void this invoice? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/v1/billing/admin/invoices/${invoiceId}/void`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        loadInvoices();
        setSelectedInvoice(null);
        toast.success('Invoice voided successfully', {
          duration: 3000,
          icon: '✅',
        });
      } else {
        toast.error('Failed to void invoice', {
          duration: 5000,
          icon: '❌',
        });
      }
    } catch (error) {
      console.error('Failed to void invoice', error);
      toast.error('Error voiding invoice', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      DRAFT: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      OPEN: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      PAID: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      VOID: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      UNCOLLECTIBLE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const filteredInvoices = filter === 'all'
    ? invoices
    : invoices.filter(inv => inv.status === filter);

  // Paginate results
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const totalRevenue = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + Number(inv.total || 0), 0);

  const pendingAmount = invoices
    .filter(inv => inv.status === 'pending' || inv.status === 'draft')
    .reduce((sum, inv) => sum + Number(inv.total || 0), 0);

  return (
    <MainLayout>
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Invoices</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            View all invoices and manage manual payments
          </p>
        </div>
      </div>

      <div className="mb-6">
        <BillingSystemStatus isAdmin={true} />
      </div>

      {/* Statistics */}
      <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Revenue</div>
          <div className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
            ${totalRevenue.toFixed(2)}
          </div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending</div>
          <div className="mt-2 text-3xl font-bold text-yellow-600 dark:text-yellow-400">
            ${pendingAmount.toFixed(2)}
          </div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Paid Invoices</div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {invoices.filter(inv => inv.status === 'paid').length}
          </div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Open Invoices</div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {invoices.filter(inv => inv.status === 'pending' || inv.status === 'draft').length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        {[
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'paid', label: 'Paid' },
          { value: 'void', label: 'Void' },
        ].map((status) => (
          <button
            key={status.value}
            onClick={() => setFilter(status.value as any)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filter === status.value
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            {status.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Due Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                    </div>
                  </td>
                </tr>
              ) : paginatedInvoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No invoices found
                  </td>
                </tr>
              ) : (
                paginatedInvoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    onClick={() => setSelectedInvoice(invoice)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <code className="rounded bg-gray-100 px-2 py-1 text-sm font-mono text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                        {invoice.invoice_number}
                      </code>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex flex-col">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {invoice.tenant.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {invoice.tenant.subdomain}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {invoice.subscription.plan.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="font-medium text-gray-900 dark:text-white">
                        ${Number(invoice.total || 0).toFixed(2)}
                      </div>
                      {Number(invoice.tax || 0) > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          (Tax: ${Number(invoice.tax || 0).toFixed(2)})
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {getStatusBadge(invoice.status)}
                      {invoice.paid_at && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Paid {new Date(invoice.paid_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(invoice.due_date).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        {invoice.status === 'OPEN' && (
                          <>
                            <button
                              onClick={() => handleMarkAsPaid(invoice.id)}
                              className="rounded-md bg-green-600 px-3 py-1.5 text-white hover:bg-green-700"
                              title="Mark as paid (for manual payments)"
                            >
                              Mark Paid
                            </button>
                            <button
                              onClick={() => handleVoid(invoice.id)}
                              className="rounded-md bg-red-600 px-3 py-1.5 text-white hover:bg-red-700"
                            >
                              Void
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredInvoices.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={filteredInvoices.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 rounded-lg border-l-4 border-indigo-500 bg-indigo-50 p-4 dark:bg-indigo-900/20">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-300">
              Manual Payment Processing
            </h3>
            <div className="mt-2 text-sm text-indigo-700 dark:text-indigo-400">
              <p>
                Use "Mark Paid" for customers who pay outside Stripe (bank transfer, check, etc.).
                This will record the payment and activate their subscription without Stripe processing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedInvoice(null)}>
          <div className="max-w-3xl w-full rounded-lg bg-white p-6 dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                Invoice Details
              </h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Header Info */}
              <div className="flex items-start justify-between border-b pb-4 dark:border-gray-700">
                <div>
                  <div className="flex items-center gap-3">
                    <code className="rounded bg-gray-100 px-3 py-1.5 text-lg font-mono font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      {selectedInvoice.invoice_number}
                    </code>
                    {getStatusBadge(selectedInvoice.status)}
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Created: {new Date(selectedInvoice.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    ${Number(selectedInvoice.total || 0).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Amount</p>
                </div>
              </div>

              {/* Customer & Plan Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Customer
                  </label>
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {selectedInvoice.tenant.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedInvoice.tenant.subdomain}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Plan
                  </label>
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {selectedInvoice.subscription.plan.name}
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Details */}
              {selectedInvoice.usage_details && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Usage Details
                  </label>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Infrastructure Hosts</span>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {selectedInvoice.usage_details.avg_infra_hosts.toFixed(1)} avg
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="text-sm text-gray-600 dark:text-gray-400">APM Hosts</span>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {selectedInvoice.usage_details.avg_apm_hosts.toFixed(1)} avg
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Log Events</span>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {(selectedInvoice.usage_details.log_events / 1000000).toFixed(2)}M
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Metrics Events</span>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {(selectedInvoice.usage_details.metrics_events / 1000000).toFixed(2)}M
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Trace Spans</span>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {(selectedInvoice.usage_details.trace_spans / 1000000).toFixed(2)}M
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                          </svg>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Storage Used</span>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {selectedInvoice.usage_details.storage_gb.toFixed(2)} GB
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Amount Breakdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount Breakdown
                </label>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between px-4 py-3 border-b dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${Number(selectedInvoice.subtotal || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between px-4 py-3 border-b dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Tax (5%)</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${Number(selectedInvoice.tax || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50">
                    <span className="font-semibold text-gray-900 dark:text-white">Total</span>
                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                      ${Number(selectedInvoice.total || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Issued Date
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {new Date(selectedInvoice.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Due Date
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                {selectedInvoice.paid_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Paid Date
                    </label>
                    <div className="text-sm font-medium text-green-600 dark:text-green-400">
                      {new Date(selectedInvoice.paid_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t pt-4 dark:border-gray-700">
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Close
                </button>
                {(selectedInvoice.status === 'OPEN' || selectedInvoice.status === 'pending') && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsPaid(selectedInvoice.id);
                      }}
                      className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                    >
                      Mark as Paid
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVoid(selectedInvoice.id);
                      }}
                      className="rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                    >
                      Void Invoice
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </MainLayout>
  );
}
